"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Calendar, ChevronDown, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ANewChapterBegins() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: actionsRef,
    isVisible: actionsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: trustRef,
    isVisible: trustVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const trustMetrics = [{
    value: "June 15",
    label: "The Big Day"
  }, {
    value: "11:00 AM",
    label: "Brunch Time"
  }, {
    value: "The Estate",
    label: "Venue Location"
  }];
  return <section data-section-id="609" id="a-new-chapter-begins" className="relative overflow-hidden bg-background text-foreground">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute top-8 right-8 hidden h-40 w-40 opacity-5 md:block lg:h-56 lg:w-56" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1" style={{
      color: "var(--primary)"
    }}>
        <text x="10" y="150" className="font-serif text-[180px] font-medium opacity-10">O</text>
      </svg>

      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 pt-16 pb-20 sm:px-10 lg:grid-cols-[60fr_40fr] lg:gap-16 lg:px-16 lg:pt-24 lg:pb-28 xl:px-24 xl:pl-24">
        {}
        <div className="flex flex-col justify-center lg:pr-8">
          {}
          <div ref={labelRef} className={`flex items-center gap-3 transition-all duration-700 ease-out ${labelVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <span className="h-px w-8" style={{
            backgroundColor: "var(--primary)"
          }} />
            <Badge variant="outline" className="border-0 bg-transparent px-0 py-0 text-xs font-bold tracking-[0.2em] uppercase font-default" style={{
            color: "var(--primary)"
          }}>Welcome Baby Ohlson • 2026</Badge>
          </div>

          {}
          <h1 ref={headlineRef} className={`mt-6 font-serif leading-[1.1] tracking-tighter text-foreground transition-all delay-100 duration-700 ease-out ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`} style={{
          fontSize: "clamp(2.5rem, 8vw, 5rem)"
        }}>
            A new chapter{" "}
            <em className="font-serif font-normal italic" style={{
            color: "var(--primary)"
          }}>
              begins
            </em>{" "}
            this summer.
          </h1>

          {}
          <p ref={bodyRef} className={`mt-8 max-w-[480px] text-lg leading-relaxed font-default text-muted-foreground transition-all delay-200 duration-700 ease-out ${bodyVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            Join us for a morning of celebration as we prepare to welcome the newest member 
            of the Ohlson family. A curated brunch, sweet memories, and a toast to the 
            beautiful journey of parenthood that lies ahead.
          </p>

          {}
          <div ref={actionsRef} className={`mt-10 flex flex-col items-start gap-4 transition-all delay-300 duration-700 ease-out sm:flex-row sm:items-center sm:gap-6 ${actionsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <Link to="/EventDetailsPage">
              <Button size="lg" className="h-12 gap-2 rounded-md bg-primary px-7 text-base font-medium text-primary-foreground shadow-md hover:opacity-90">
                <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                View Registry
              </Button>
            </Link>
            <Link to="/OurJourneyPage" className="group inline-flex items-center gap-1 text-base font-medium text-foreground">
              <span className="underline-offset-[6px] transition-all group-hover:underline font-default" style={{
              textDecorationColor: "var(--primary)"
            }}>
                Our maternity story
              </span>
              <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">
                →
              </span>
            </Link>
          </div>

          {}
          <div ref={trustRef} className={`mt-12 transition-all delay-500 duration-700 ease-out ${trustVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="h-px w-full" style={{
            backgroundColor: "var(--border)"
          }} />
            <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-3 sm:gap-x-4">
              {trustMetrics.map((metric, i) => <div data-index={i} key={metric.value} className="flex items-center gap-2 sm:gap-4">
                  {i > 0 && <span aria-hidden="true" className="inline-block size-1.5 rounded-md" style={{
                backgroundColor: "var(--primary)"
              }} />}
                  <div className="flex flex-col">
                    <span className="font-serif text-lg font-semibold text-foreground">
                      {metric.value}
                    </span>
                    <span className="text-xs tracking-widest text-muted-foreground uppercase font-default">
                      {metric.label}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div className="relative flex items-center justify-center lg:justify-end">
          <div ref={imageRef} className="relative w-full max-w-md lg:max-w-none">
            {}
            <div className="relative overflow-hidden shadow-xl" style={{
            borderTopLeftRadius: "0rem",
            borderTopRightRadius: "0rem",
            borderBottomLeftRadius: "0rem",
            borderBottomRightRadius: "0rem",
            clipPath: imageVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
            transition: "clip-path 900ms cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_portrait_ohlson.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_portrait_ohlson.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694181/img/maternity_portrait_ohlson.png-wvc-sizes" alt="Expectant parents maternity portrait" loading="lazy" decoding="async" className="aspect-[3/4] w-full object-cover grayscale-[20%]" />
            </div>

            {}
            <div className={`absolute -bottom-6 -left-4 flex items-center gap-3 rounded-md bg-card px-5 py-4 shadow-md transition-all delay-700 duration-700 ease-out sm:-left-8 border border-border ${imageVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
              <span className="relative flex size-3 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-md opacity-75" style={{
                backgroundColor: "var(--primary)"
              }} />
                <span className="relative inline-flex size-2.5 rounded-md" style={{
                backgroundColor: "var(--primary)"
              }} />
              </span>
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.15em] text-muted-foreground uppercase font-default">
                  RSVP Status
                </span>
                <span className="font-serif text-base font-semibold text-card-foreground">
                  Open until June 1st
                </span>
              </div>
            </div>

            {}
            <div className={`absolute -top-4 -right-3 flex size-24 flex-col items-center justify-center rounded-md p-3 text-center shadow-md transition-all delay-700 duration-700 ease-out sm:size-28 sm:-right-6 ${imageVisible ? "scale-100 opacity-100" : "scale-90 opacity-0"}`} style={{
            backgroundColor: "var(--card)",
            color: "var(--primary)",
            borderRadius: "0rem",
            border: "1px solid var(--border)"
          }}>
              <Phone className="mb-1 size-4" />
              <span className="font-serif text-sm leading-tight font-semibold">RSVP</span>
              <span className="text-[10px] leading-tight font-default">Digital Form</span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="flex justify-center pb-8">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <span className="text-[10px] tracking-[0.25em] uppercase font-default">Explore</span>
          <ChevronDown className="size-4 animate-bounce" aria-hidden="true" />
        </div>
      </div>
    </section>;
}